import {
	closestCenter,
	DndContext,
	type DragEndEvent,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors,
} from "@dnd-kit/core";
import {
	arrayMove,
	SortableContext,
	sortableKeyboardCoordinates,
	useSortable,
	verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { format } from "date-fns";
import { ChevronUp, GripVertical } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";

import { type Matter, useCaseListCache, useMattersCache } from "@/shared/cache";
import type { Entry } from "@/shared/types/entries";
import {
	matterDisplayLabel,
	matterSearchFields,
	matterSearchValue,
} from "@/shared/utils/matterLabels";
import { getRecentMatters, searchMatters } from "@/shared/utils/matterSearch";
import {
	renderMatterItem,
	renderSelectedMatter,
} from "@/v2/components/entry-form/pickerItems";
import { Badge } from "@/v2/components/ui/badge";
import { ComboBox } from "@/v2/components/ui/combobox";
import { Label } from "@/v2/components/ui/label";
import {
	ResponsiveDialog,
	ResponsiveDialogContent,
} from "@/v2/components/ui/responsive-dialog";
import { TooltipButton } from "@/v2/components/ui/tooltip-button";
import { AppIcon } from "@/v2/lib/appIcons";
import { parseEntryDate } from "@/v2/lib/dates";

export interface CombineDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	entries: Entry[];
	onConfirm: (ids: string[], matterId: string, mainEntryId: string) => void;
}

function formatUnits(units: number): string {
	const two = units.toFixed(2);
	return two.endsWith("0") ? units.toFixed(1) : two;
}

/** "HH:mm" → "h:mm AM/PM" (same rendering as the entries table time column). */
function formatEndTime(t: string | null): string | null {
	if (!t) return null;
	const [hStr, mStr] = t.split(":");
	const h = Number(hStr);
	const m = Number(mStr);
	if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
	const period = h >= 12 ? "PM" : "AM";
	const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
	return `${hour12}:${String(m).padStart(2, "0")} ${period}`;
}

function SortableEntryRow({
	entry,
	isMain,
	onPromote,
}: {
	entry: Entry;
	isMain: boolean;
	onPromote: () => void;
}) {
	const {
		attributes,
		listeners,
		setNodeRef,
		transform,
		transition,
		isDragging,
	} = useSortable({ id: entry.id });

	// Lock drag to vertical axis only — zero out X to prevent horizontal scroll
	const yOnlyTransform = transform ? { ...transform, x: 0 } : transform;

	const endTime = formatEndTime(entry.endTime);

	return (
		<div
			ref={setNodeRef}
			style={{
				transform: CSS.Translate.toString(yOnlyTransform),
				transition,
			}}
			{...attributes}
			{...listeners}
			className={`flex items-center gap-3 border-b border-border/40 px-4 py-2.5 text-[13px] min-h-12 cursor-default touch-none select-none border-l-2 ${
				isDragging ? "opacity-50 z-10 relative" : ""
			} ${isMain ? "border-l-primary bg-primary/5" : "border-l-transparent"}`}
		>
			{/* Left slot — "Main" label or promote arrow, fixed width for alignment */}
			<div className="shrink-0 w-10 flex items-center justify-center">
				{isMain ? (
					<span className="rounded bg-primary/15 px-1 py-0.5 text-[10px] font-semibold text-primary">
						Main
					</span>
				) : (
					<button
						type="button"
						onClick={(e) => {
							e.stopPropagation();
							onPromote();
						}}
						onPointerDown={(e) => e.stopPropagation()}
						onKeyDown={(e) => {
							e.stopPropagation();
							if (e.key === "Enter") {
								e.preventDefault();
								onPromote();
							}
						}}
						className="rounded p-0.5 text-muted-foreground/60 transition-colors hover:bg-accent hover:text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
						title="Set as main entry"
					>
						<ChevronUp className="size-3.5" />
					</button>
				)}
			</div>

			{/* Drag grip indicator (visual only) */}
			<GripVertical className="size-3.5 shrink-0 text-muted-foreground/40" />

			<AppIcon
				application={entry.application}
				className="size-6 shrink-0 text-muted-foreground"
			/>

			{/* Date + end time */}
			<div className="flex shrink-0 flex-col text-[10px] font-semibold tabular-nums text-muted-foreground">
				<span>{format(parseEntryDate(entry.date), "MM/dd")}</span>
				{endTime && (
					<span className="font-normal text-muted-foreground/70">
						{endTime}
					</span>
				)}
			</div>

			{/* Narrative */}
			<div className="mr-3 min-w-0 flex-1">
				<span className="whitespace-normal break-words leading-snug">
					{entry.description}
				</span>
			</div>

			{/* Units badge — same rendering as the entries table units column */}
			<Badge className="min-w-[50px] shrink-0 justify-center rounded-[7px] bg-foreground px-2.5 py-1.5 text-[0.8125rem] font-[525] leading-none tabular-nums text-background">
				{formatUnits(entry.units)}
			</Badge>
		</div>
	);
}

export function CombineDialog({
	open,
	onOpenChange,
	entries,
	onConfirm,
}: CombineDialogProps) {
	const { matters } = useMattersCache();
	// Same picker behavior as the add/edit entry forms: the default list shows
	// the user's recent matters; searching spans the whole firm with case-list
	// matters ranked first.
	const { caseListIds } = useCaseListCache();
	const searchMattersForUser = useCallback(
		(items: Matter[], query: string) =>
			searchMatters(items, query, caseListIds),
		[caseListIds],
	);
	// Ordered entry ids — the first one is the main entry
	const [orderedIds, setOrderedIds] = useState<string[]>([]);
	const [selectedMatterId, setSelectedMatterId] = useState<string | null>(null);

	// Reset state when the dialog closes so the next open starts fresh
	useEffect(() => {
		if (!open) {
			setOrderedIds([]);
			setSelectedMatterId(null);
			return;
		}

		setOrderedIds(entries.map((e) => e.id));

		const uniqueMatterIds = new Set(
			entries.flatMap((e) => (e.matter?.id ? [e.matter.id] : [])),
		);
		setSelectedMatterId(
			uniqueMatterIds.size === 1 ? ([...uniqueMatterIds][0] ?? null) : null,
		);
	}, [open, entries]);

	const mainEntryId = orderedIds[0] ?? null;

	const combinedTotal = useMemo(
		() => entries.reduce((sum, e) => sum + (e.units || 0), 0),
		[entries],
	);

	const sensors = useSensors(
		useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		}),
	);

	const handleDragEnd = (event: DragEndEvent) => {
		const { active, over } = event;
		if (over && active.id !== over.id) {
			setOrderedIds((prev) => {
				const oldIndex = prev.indexOf(active.id as string);
				const newIndex = prev.indexOf(over.id as string);
				return arrayMove(prev, oldIndex, newIndex);
			});
		}
	};

	const promoteToMain = (id: string) => {
		setOrderedIds((prev) => [id, ...prev.filter((i) => i !== id)]);
	};

	const orderedEntries = useMemo(
		() =>
			orderedIds
				.map((id) => entries.find((e) => e.id === id))
				.filter((e): e is Entry => e !== undefined),
		[orderedIds, entries],
	);

	const canConfirm = Boolean(selectedMatterId && mainEntryId);

	return (
		<ResponsiveDialog open={open} onOpenChange={onOpenChange}>
			<ResponsiveDialogContent
				className="sm:max-w-3xl"
				title="Combine Entries"
				subtitle="Select a matter and drag to reorder. The top entry drives the combined narrative."
				footer={
					<div className="flex items-center justify-between gap-3">
						<div className="text-sm text-muted-foreground">
							Combined total:{" "}
							<span className="font-medium text-foreground">
								{combinedTotal.toFixed(1)} hrs
							</span>
						</div>
						<div className="flex items-center gap-2">
							<TooltipButton
								variant="outline"
								size="sm"
								onClick={() => onOpenChange(false)}
								tooltip="Cancel"
							>
								Cancel
							</TooltipButton>
							<TooltipButton
								size="sm"
								disabled={!canConfirm}
								tooltip="Combine entries"
								onClick={() => {
									if (!selectedMatterId || !mainEntryId) return;
									onConfirm(orderedIds, selectedMatterId, mainEntryId);
								}}
							>
								Combine
							</TooltipButton>
						</div>
					</div>
				}
			>
				<div className="flex flex-col gap-3 p-4">
					<div className="grid w-full gap-1.5">
						<Label htmlFor="combine-matter">Matter</Label>
						<ComboBox
							items={matters}
							value={selectedMatterId}
							onChange={setSelectedMatterId}
							fields={matterSearchFields}
							getDefaultItems={getRecentMatters}
							searchItems={searchMattersForUser}
							getKey={(m) => m.matter_id}
							getDisplayLabel={matterDisplayLabel}
							getSearchValue={matterSearchValue}
							renderItem={renderMatterItem}
							renderSelected={renderSelectedMatter}
							placeholder="Select matter..."
							error={!selectedMatterId ? "Matter is required" : undefined}
						/>
					</div>

					<div className="max-h-[340px] overflow-y-auto overflow-x-hidden rounded-md border border-border">
						<DndContext
							sensors={sensors}
							collisionDetection={closestCenter}
							onDragEnd={handleDragEnd}
						>
							<SortableContext
								items={orderedIds}
								strategy={verticalListSortingStrategy}
							>
								{orderedEntries.map((entry, index) => (
									<SortableEntryRow
										key={entry.id}
										entry={entry}
										isMain={index === 0}
										onPromote={() => promoteToMain(entry.id)}
									/>
								))}
							</SortableContext>
						</DndContext>
					</div>
				</div>
			</ResponsiveDialogContent>
		</ResponsiveDialog>
	);
}
